import { useEffect, useState } from 'react'
import { EnhancedPaper, useAssetsContext } from 'summon-ui'
import { useTranslation } from 'summon-ui/intl'
import { Flex, Image, Paper, Progress, Stack, Text, Title, Button, Modal, Box, Center } from 'summon-ui/mantine'
import { useAccount, useReadContract } from 'wagmi'
import { graphqlClient } from '@api/config'
import SocialSharingModal from '@components/SocialSharingModal/SocialSharingModal'
import { TENANT_CONFIG, TENANT_CONTRACTS } from '@config'
import { LEVEL_ABI } from '@constants/Abi/levelAbi'
import LevelUpModal from '@features/home/components/levelUp/LevelUpModal'
import OnChainLevelUpModal from '@features/home/components/levelUp/OnChainLevelUpModal'
import { useGetUserQuery } from '@generated/generates'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { isStaging } from '@utils/isStaging'

interface Props {
  onChain: boolean
}

const LevelInfo = ({ onChain = false }: Props) => {
  const t = useTranslation()
  const { address } = useAccount()
  const [openLevel, setOpenLevel] = useState(false)
  const { data: user, isLoading } = useGetUserQuery(graphqlClient())
  const [isSocialSharingModalOpen, setIsSocialSharingModalOpen] = useState(false)
  const [lastLevelUpLevel, setLastLevelUpLevel] = useLocalStorage('lastLevel', 0)

  const currentLevelInfo = user?.viewer?.currentLevelInfo
  const totalLevelXp = currentLevelInfo?.totalLevelRequiredXp
  const userXp = user?.viewer?.xp
  const totalLevelQuests = currentLevelInfo?.totalLevelRequiredQuests
  const userLevelQuests = currentLevelInfo?.completedRequiredQuests

  const { data: level, refetch: refetchReadOnchainLevel } = useReadContract({
    address: isStaging ? TENANT_CONTRACTS.testnet.level : TENANT_CONTRACTS.mainnet.level,
    abi: LEVEL_ABI,
    chainId: TENANT_CONFIG.network.id,
    functionName: 'getMyLevel',
    account: address as `0x${string}`,
    query: {
      enabled: onChain
    }
  })
  const currentLevel = user?.viewer?.level as number

  const actualLevel = onChain ? Number(level) : lastLevelUpLevel
  const { getTenantImage } = useAssetsContext()
  const levelImage = getTenantImage(`level-${actualLevel}`)

  const isAbleToLevel = actualLevel < currentLevel

  const isAbleToShareAvatar = TENANT_CONFIG.features.enableShareAvatar
  const QuestPercentage = Math.min(((userLevelQuests as number) / (totalLevelQuests as number)) * 100, 100)

  const currentXpPercentage =
    currentLevelInfo?.userLevelXp != null && currentLevelInfo?.minXpToNextLevel != null
      ? Math.min((currentLevelInfo.userLevelXp / currentLevelInfo.minXpToNextLevel) * 100, 100)
      : 0
  const progressPercentage = (currentXpPercentage + QuestPercentage) / 2 || 0
  const displayProgressPercentage = progressPercentage > 100 ? 100 : progressPercentage

  const noMoreLevels = !totalLevelXp && !isAbleToLevel
  const showLevelUp = () => {
    setOpenLevel(true)
  }

  const handleCloseOnchainLevelUpModal = () => {
    refetchReadOnchainLevel()
    setOpenLevel(false)
    isAbleToShareAvatar && setIsSocialSharingModalOpen(true)
  }

  const handleCloseLevelUpModal = () => {
    setLastLevelUpLevel(currentLevel)
    setOpenLevel(false)
    isAbleToShareAvatar && setIsSocialSharingModalOpen(true)
  }

  useEffect(() => {
    if (lastLevelUpLevel > currentLevel) setLastLevelUpLevel(currentLevel)
  }, [currentLevel])

  return (
    <Stack p={{ xs: 'md', md: '0' }} gap='sm'>
      <Title data-testid='level-info-title-label' fz='md' visibleFrom='sm'>
        {t('Level Info')}
      </Title>
      {isLoading ? (
        <Paper withBorder h={224} radius='md' bg='gray.3' className='animate-pulse' />
      ) : (
        <EnhancedPaper withBorder p='md' radius='md' w='100%' withBg>
          <Flex justify='space-between' align='end'>
            <Flex gap='sm'>
              {levelImage ? (
                <Image data-testid='level-info-level-image' alt='level icon' h='48' src={levelImage} />
              ) : (
                <Box w={48} h={48} bg='brand' className='rounded-full'>
                  <Center w='100%' h='100%'>
                    <Text c='white' size='40' mt='xs' className='font-gibson'>
                      {actualLevel}
                    </Text>
                  </Center>
                </Box>
              )}
              <Stack justify='center' gap='0'>
                <Title data-testid='level-info-actual-level-label' fz='md'>
                  {t('Level')} {actualLevel}
                </Title>
                <Text data-testid='level-info-progress-label' fz='sm'>
                  {isAbleToLevel ? 100 : displayProgressPercentage.toFixed(0)}% complete
                </Text>
              </Stack>
            </Flex>
          </Flex>
          {!noMoreLevels ? (
            <>
              <Flex my='md' direction={{ md: 'column' }}>
                <Flex className='flex justify-between flex-col md:flex-row w-1/2 gap-2 md:w-full md:pb-4'>
                  <Text>{t('Total XP')}</Text>
                  <Flex gap='sm' align='center' justify='space-between' w={150}>
                    <Progress color='green' value={isAbleToLevel ? 100 : currentXpPercentage} size='md' w={100} />
                    {!isAbleToLevel && (
                      <Text data-testid='level-info-xp-label'>
                        {userXp}/{totalLevelXp}
                      </Text>
                    )}
                  </Flex>
                </Flex>
                <Flex className='flex justify-between md:pt-5 flex-col md:flex-row w-1/2 gap-2 md:w-full'>
                  <Text>{t('Required Actions')}</Text>
                  <Flex gap='sm' align='center' justify='space-between' w={150}>
                    <Progress color='green' value={isAbleToLevel ? 100 : QuestPercentage} size='md' w={100} />
                    {!isAbleToLevel && (
                      <Text>
                        {userLevelQuests}/{totalLevelQuests}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              </Flex>

              {!!isAbleToLevel && (
                <>
                  <Button fullWidth mt='sm' onClick={showLevelUp}>
                    {onChain ? t('Level Up & Claim') : t('Level Up')}
                  </Button>
                  {onChain ? (
                    <Modal
                      opened={openLevel}
                      onClose={handleCloseOnchainLevelUpModal}
                      withCloseButton={false}
                      radius='md'
                      size='xl'
                    >
                      <OnChainLevelUpModal
                        open={openLevel}
                        close={handleCloseOnchainLevelUpModal}
                        currentLevel={actualLevel}
                      />
                    </Modal>
                  ) : (
                    <LevelUpModal open={openLevel} close={() => handleCloseLevelUpModal()} currentLevel={actualLevel} />
                  )}
                </>
              )}
            </>
          ) : (
            <div className='flex w-full items-center h-[70px]'>{t('New levels coming soon')}...</div>
          )}
        </EnhancedPaper>
      )}
      {isSocialSharingModalOpen && <SocialSharingModal variant='avatar' isOpened asButton={false} />}
    </Stack>
  )
}

export default LevelInfo
